'use client';

import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import { useParams } from 'react-router-dom';

import {
    Chart,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
    LineController,
    TimeScale,
    TimeSeriesScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
    ChevronDownIcon,
    CalendarDaysIcon,
    ArrowPathIcon,
    ChartPieIcon,
    ChartBarSquareIcon,
    WrenchScrewdriverIcon,
    QuestionMarkCircleIcon,
    ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';

Chart.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend,
    Filler,
    LineController,
    TimeScale,
    TimeSeriesScale
);

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

// Spruced-up StatCard component with dynamic icons and layout
const StatCard = ({ title, value, unit, description, icon: Icon, type = 'default', min, max }) => {
    return (
        <div className="flex items-center space-x-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex-shrink-0 p-3 bg-gray-100 rounded-full">
                <Icon className="h-6 w-6 text-gray-700" />
            </div>
            <div className="flex-1">
                <p className="text-sm font-medium text-gray-500">{title}</p>
                {type === 'range' ? (
                    <div className="mt-1 flex items-baseline space-x-2">
                        <p className="text-xl font-bold text-gray-900">{min}</p>
                        <span className="text-xl font-bold text-gray-900">-</span>
                        <p className="text-xl font-bold text-gray-900">{max}</p>
                        {unit && <span className="ml-1 text-base font-normal text-gray-500">{unit}</span>}
                    </div>
                ) : (
                    <p className="text-2xl font-bold mt-1 text-gray-900">
                        {value}
                        {unit && <span className="text-lg font-normal ml-1 text-gray-500">{unit}</span>}
                    </p>
                )}
                {description && <p className="text-xs mt-1 text-gray-500">{description}</p>}
            </div>
        </div>
    );
};

// Tooltip Component
const Tooltip = ({ children, content }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);

    const showTooltip = (e) => {
        if (!content || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
            top: rect.bottom + window.scrollY + 5,
            left: rect.left + window.scrollX + rect.width / 2
        });
        setIsVisible(true);
    };

    const hideTooltip = () => {
        setIsVisible(false);
    };

    const tooltipElement = isVisible && content ? (
        <div
            className="fixed z-[10000] px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg pointer-events-none transform -translate-x-1/2"
            style={{
                top: position.top,
                left: position.left,
                maxWidth: '200px'
            }}
        >
            {content}
            <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
        </div>
    ) : null;

    return (
        <>
            <span ref={triggerRef} onMouseEnter={showTooltip} onMouseLeave={hideTooltip} className="cursor-help">
                {children}
            </span>
            {tooltipElement && createPortal(tooltipElement, document.body)}
        </>
    );
};

// Custom Date Filter Dropdown Component
const CustomDateSelector = ({ isOpen, onClose, buttonRef, startDate, endDate, onStartDateChange, onEndDateChange, onPresetSelect }) => {
    const dropdownRef = useRef(null);

    const dropdownPosition = useMemo(() => {
        if (!isOpen || !buttonRef.current) return {};
        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = 450;
        let left = rect.left;

        if (left + dropdownWidth > window.innerWidth) {
            left = rect.right - dropdownWidth;
        }
        if (left < 0) left = 8;

        return { top: rect.bottom + 8, left, width: dropdownWidth };
    }, [isOpen, buttonRef]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target) && buttonRef.current && !buttonRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose, buttonRef]);

    const presets = [
        { label: 'All Time', value: 'all' },
        { label: 'Last 7 days', value: '7days' },
        { label: 'Last 30 days', value: '30days' },
        { label: 'This Month', value: 'thisMonth' },
    ];

    if (!isOpen) return null;

    return createPortal(
        <div ref={dropdownRef} className="fixed z-[999] bg-white border border-gray-200 rounded-lg shadow-xl" style={dropdownPosition}>
            <div className="flex">
                <div className="w-40 border-r border-gray-200 bg-gray-50 p-2">
                    <div className="space-y-1">
                        {presets.map((preset) => (
                            <button key={preset.value} onClick={() => onPresetSelect(preset.value)} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-md transition-colors">
                                {preset.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex-1 p-4">
                    <p className="text-sm font-medium text-gray-800 mb-3">Custom Range</p>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                            <input type="date" value={startDate} onChange={(e) => onStartDateChange(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                            <input type="date" value={endDate} onChange={(e) => onEndDateChange(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                    </div>
                </div>
            </div>
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button onClick={onClose} className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50">Close</button>
            </div>
        </div>,
        document.body
    );
};

// Main Trend Chart Component
const MetricTrendChart = ({ data, metric, stats, onDateSelect, metricThresholds }) => {
    const chartRef = useRef(null);
    const chartId = useMemo(() => `chart-${metric.dataKey}`, [metric.dataKey]);

    useEffect(() => {
        const canvas = chartRef.current;
        if (!canvas) return;

        const existingChart = Chart.getChart(chartId);
        if (existingChart) {
            existingChart.destroy();
        }

        if (data.length === 0) return;

        const ctx = canvas.getContext('2d');
        const labels = data.map(d => new Date(d.LocalTimeOfMeasurement));
        const yValues = data.map(d => d[metric.dataKey]);
        const datasets = [{
            label: metric.label,
            data: yValues,
            borderColor: '#4f46e5',
            backgroundColor: 'rgba(79, 70, 229, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            fill: true,
            pointRadius: data.length > 200 ? 0 : 3,
            pointHoverRadius: 5,
        }];

        if (stats.mean) {
            datasets.push({
                label: 'Mean',
                data: new Array(data.length).fill(stats.mean),
                borderColor: '#f59e0b',
                borderWidth: 1.5,
                borderDash: [5, 5],
                pointRadius: 0,
                fill: false,
            });
        }

        const threshold = metricThresholds[metric.dataKey];
        console.log('Rendering chart for metric:', metric.dataKey);
        console.log('Threshold value:', threshold);
        if (threshold !== null && threshold !== undefined) {
            datasets.push({
                label: `Threshold (${threshold})`,
                data: new Array(data.length).fill(threshold),
                borderColor: '#ef4444',
                borderWidth: 1.5,
                borderDash: [5, 5],
                pointRadius: 0,
                fill: false,
            });
        } else {
            console.log('Skipping threshold dataset because threshold is null or undefined');
        }
        console.log('Datasets:', datasets);

        new Chart(ctx, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                onClick: (event, elements) => {
                    if (elements.length > 0 && elements[0].datasetIndex === 0) {
                        const pointIndex = elements[0].index;
                        const date = labels[pointIndex];
                        if (date && onDateSelect) {
                            const dateString = date.toISOString().split('T')[0];
                            onDateSelect({ startDate: dateString, endDate: dateString });
                        }
                    }
                },
                scales: {
                    x: {
                        type: 'category',
                        title: { display: true, text: 'Measurement Point' },
                        ticks: {
                            autoSkip: true,
                            maxRotation: 45,
                            minRotation: 0,
                            maxTicksLimit: 10,
                            callback: function (value) {
                                const label = this.getLabelForValue(value);
                                return new Date(label).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            }
                        }
                    },
                    y: { title: { display: true, text: `${metric.label} (${metric.unit})` } }
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: function (tooltipItems) {
                                const index = tooltipItems[0].dataIndex;
                                const date = labels[index];
                                return date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
                            },
                            label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)} ${metric.unit}`,
                        }
                    }
                }
            }
        });

    }, [data, metric, stats, onDateSelect, metricThresholds, chartId]);

    return (
        <div className="bg-white p-6 rounded-lg border relative shadow-sm">
            <div className="h-[450px]">
                <canvas ref={chartRef} id={chartId}></canvas>
                {data.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center h-full text-gray-500 pointer-events-none">
                        No data available for the selected period.
                    </div>
                )}
            </div>
            
        </div>
    );
};

// Main Analytics Page Component
export default function ProjectAnalytics() {
    const { user } = useContext(AuthContext);
    const { projectId } = useParams();
    const [measurements, setMeasurements] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedMetric, setSelectedMetric] = useState('compaction');
    const [metricThresholds, setMetricThresholds] = useState({
        Compaction: 92,
        Density: 145,
        Voids: 7,
        SurfaceTemp: 350
    });

    const [dateRange, setDateRange] = useState('all');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [isDateFilteredByChart, setIsDateFilteredByChart] = useState(false);
    const dateButtonRef = useRef(null);

    useEffect(() => {
        console.log('metricThresholds:', metricThresholds);
    }, [metricThresholds]);

    useEffect(() => {
        const fetchMeasurements = async () => {
            if (!user || !projectId) return;
            setLoading(true);
            try {
                const { data, error } = await supabase
                    .from('measurements_with_details')
                    .select('LocalTimeOfMeasurement, Density, Compaction, Voids, SurfaceTemp')
                    .eq('project_id', projectId)
                    .order('LocalTimeOfMeasurement', { ascending: true });

                if (error) throw error;
                setMeasurements(data || []);
            } catch (err) {
                console.error('Error fetching measurements:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchMeasurements();
    }, [user, projectId]);

    const filteredMeasurements = useMemo(() => {
        if (!measurements.length) return [];
        let filtered = measurements;
        if (dateRange === 'all') return filtered;

        const end = new Date();
        let start;

        if (dateRange === '7days') {
            start = new Date();
            start.setDate(end.getDate() - 7);
        } else if (dateRange === '30days') {
            start = new Date();
            start.setDate(end.getDate() - 30);
        } else if (dateRange === 'thisMonth') {
            start = new Date(end.getFullYear(), end.getMonth(), 1);
        } else if (dateRange === 'custom' && customStartDate && customEndDate) {
            start = new Date(customStartDate);
            start.setUTCHours(0, 0, 0, 0);
            const customEnd = new Date(customEndDate);
            customEnd.setUTCHours(23, 59, 59, 999);
            return measurements.filter(m => {
                const date = new Date(m.LocalTimeOfMeasurement);
                return date >= start && date <= customEnd;
            });
        }

        if (!start) return filtered;
        return measurements.filter(m => new Date(m.LocalTimeOfMeasurement) >= start);
    }, [measurements, dateRange, customStartDate, customEndDate]);

    // Updated metricDetails to use a thresholdType for reliable pass rate calculation
    const metricDetails = useMemo(() => ({
        compaction: { label: 'Compaction', dataKey: 'Compaction', unit: '%', icon: ChartBarSquareIcon, thresholdLabel: 'Lower Limit', thresholdType: 'lower' },
        density: { label: 'Density', dataKey: 'Density', unit: '', icon: ChartBarSquareIcon, thresholdLabel: 'Lower Limit', thresholdType: 'lower' },
        voids: { label: 'Voids', dataKey: 'Voids', unit: '%', icon: ChartBarSquareIcon, thresholdLabel: 'Upper Limit', thresholdType: 'upper' },
        temperature: { label: 'Surface Temp', dataKey: 'SurfaceTemp', unit: '', icon: ChartBarSquareIcon, thresholdLabel: 'Upper Limit', thresholdType: 'upper' },
    }), []);

    const currentMetric = metricDetails[selectedMetric];

    // Updated statistics calculation to use the new thresholdType
    const statistics = useMemo(() => {
        const values = filteredMeasurements.map(m => m[currentMetric.dataKey]).filter(v => v != null && !isNaN(v));
        if (values.length === 0) return { count: 0, mean: 0, min: 0, max: 0, passRate: null };

        const sum = values.reduce((s, v) => s + v, 0);
        const mean = sum / values.length;
        const min = Math.min(...values);
        const max = Math.max(...values);

        let passRate = null;
        const threshold = metricThresholds[currentMetric.dataKey];
        console.log('Calculating stats for metric:', currentMetric.dataKey);
        console.log('Threshold value:', threshold);
        console.log('Number of values:', values.length);
        if (threshold !== null && threshold !== undefined) {
            console.log('Threshold type:', currentMetric.thresholdType);
            let passingCount;
            if (currentMetric.thresholdType === 'upper') {
                passingCount = values.filter(v => v <= threshold).length;
            } else {
                passingCount = values.filter(v => v >= threshold).length;
            }
            console.log('Passing count:', passingCount);
            passRate = (passingCount / values.length) * 100;
        } else {
            console.log('Skipping pass rate calculation because threshold is null or undefined');
        }

        return {
            count: values.length,
            mean: parseFloat(mean.toFixed(1)),
            min: parseFloat(min.toFixed(1)),
            max: parseFloat(max.toFixed(1)),
            passRate: passRate !== null ? parseFloat(passRate.toFixed(1)) : null
        };
    }, [filteredMeasurements, currentMetric, metricThresholds]);

    const handlePresetSelect = (preset) => {
        setDateRange(preset);
        setIsDateFilteredByChart(false);
        if (preset !== 'custom') {
            setCustomStartDate('');
            setCustomEndDate('');
        }
        setShowDatePicker(false);
    };

    const handleDateSelectFromChart = ({ startDate, endDate }) => {
        setDateRange('custom');
        setCustomStartDate(startDate);
        setCustomEndDate(endDate);
        setIsDateFilteredByChart(true);
        setShowDatePicker(false);
    };

    const resetDateFilter = () => {
        handlePresetSelect('all');
    };

    const dateDisplay = useMemo(() => {
        if (isDateFilteredByChart && customStartDate) {
            const date = new Date(customStartDate);
            return date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' });
        }
        if (dateRange === 'custom' && customStartDate && customEndDate) {
            const start = new Date(customStartDate);
            const end = new Date(customEndDate);
            return `${start.toLocaleDateString('en-US', { timeZone: 'UTC' })} - ${end.toLocaleDateString('en-US', { timeZone: 'UTC' })}`;
        }
        return {
            'all': 'All Time',
            '7days': 'Last 7 Days',
            '30days': 'Last 30 Days',
            'thisMonth': 'This Month',
        }[dateRange];
    }, [dateRange, customStartDate, customEndDate, isDateFilteredByChart]);

    const handleThresholdChange = (e) => {
        const value = e.target.value;
        const numberValue = value === '' ? null : Number(value);
        console.log('Changing threshold for metric:', currentMetric.dataKey, 'to:', numberValue);
        setMetricThresholds(prev => ({
            ...prev,
            [currentMetric.dataKey]: numberValue
        }));
    };

    if (loading) return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center text-gray-500">
            Loading Project Analytics...
        </div>
    );

    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            {/* Header Section */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">Project Analytics</h2>
                        <p className="text-sm text-gray-600">Metric trends and quality analysis for your project.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Menu as="div" className="relative">
                            <MenuButton className="inline-flex items-center justify-between gap-x-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-800 shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50 min-w-[150px]">
                                <span className="truncate">{currentMetric.label}</span>
                                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                            </MenuButton>
                            <MenuItems anchor="bottom end" transition className="origin-top-right z-10 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in">
                                <div className="py-1">
                                    {Object.entries(metricDetails).map(([key, metric]) => (
                                        <MenuItem key={key}>
                                            <button onClick={() => setSelectedMetric(key)} className={classNames('block w-full text-left px-4 py-2 text-sm', selectedMetric === key ? 'bg-gray-100 text-gray-900' : 'text-gray-700 data-[focus]:bg-gray-100')}>
                                                {metric.label}
                                            </button>
                                        </MenuItem>
                                    ))}
                                </div>
                            </MenuItems>
                        </Menu>
                        <button ref={dateButtonRef} onClick={() => setShowDatePicker(true)} className="inline-flex items-center justify-between gap-x-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-800 shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50 min-w-[180px]">
                            <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
                            <span className="truncate">{dateDisplay}</span>
                            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                        </button>
                        {isDateFilteredByChart && (
                            <button onClick={resetDateFilter} title="Reset Date Filter" className="p-2 text-gray-500 bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50">
                                <ArrowPathIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        title="Total Measurements"
                        value={statistics.count}
                        description="In selected period"
                        icon={ChartPieIcon}
                    />
                    <StatCard
                        title={`${currentMetric.label} Mean`}
                        value={statistics.mean}
                        unit={currentMetric.unit}
                        icon={ChartBarSquareIcon}
                    />
                    <StatCard
                        title={`${currentMetric.label} Range`}
                        type="range"
                        min={statistics.min}
                        max={statistics.max}
                        unit={currentMetric.unit}
                        icon={ArrowsRightLeftIcon}
                    />
                    <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0 p-3 bg-gray-100 rounded-full">
                                <WrenchScrewdriverIcon className="h-6 w-6 text-gray-700" />
                            </div>
                            <div className="flex-1">
                                <label htmlFor="threshold-input" className="block text-sm font-medium text-gray-500">{currentMetric.label} Threshold</label>
                                <div className="flex items-center mt-1">
                                    <input
                                        id="threshold-input"
                                        type="number"
                                        step="0.1"
                                        value={metricThresholds[currentMetric.dataKey] ?? ''}
                                        onChange={handleThresholdChange}
                                        placeholder="N/A"
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm font-bold text-gray-900 focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                    <span className="text-lg font-normal ml-2 text-gray-500">{currentMetric.unit}</span>
                                </div>
                                <p className="text-xs mt-1 text-gray-500 flex items-center">
                                    Pass Rate: <span className="font-bold ml-1">{statistics.passRate !== null ? `${statistics.passRate}%` : 'N/A'}</span>
                                    <Tooltip content="Pass rate is calculated as the percentage of measurements that meet or exceed the set threshold.">
                                        <QuestionMarkCircleIcon className="ml-1 h-3.5 w-3.5 text-gray-400" />
                                    </Tooltip>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <MetricTrendChart
                    data={filteredMeasurements}
                    metric={currentMetric}
                    stats={statistics}
                    onDateSelect={handleDateSelectFromChart}
                    metricThresholds={metricThresholds}
                />
            </div>

            {/* Portal for Date Picker */}
            <CustomDateSelector
                isOpen={showDatePicker}
                onClose={() => setShowDatePicker(false)}
                buttonRef={dateButtonRef}
                startDate={customStartDate}
                endDate={customEndDate}
                onStartDateChange={(val) => { setCustomStartDate(val); setDateRange('custom'); setIsDateFilteredByChart(false); }}
                onEndDateChange={(val) => { setCustomEndDate(val); setDateRange('custom'); setIsDateFilteredByChart(false); }}
                onPresetSelect={handlePresetSelect}
            />
        </div>
    );
}